import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { MantineProvider, localStorageColorSchemeManager } from '@mantine/core'

import { cssVariablesResolver, theme } from '@/utils/theme'

import { COLOR_SCHEME_STORAGE_KEY, PRIMARY_COLOR_STORAGE_KEY } from './constants'
import { getStoredPrimaryColor } from './utils'

const colorSchemeManager = localStorageColorSchemeManager({
  key: COLOR_SCHEME_STORAGE_KEY,
})

type PrimaryColorContextType = {
  primaryColor: string
  changePrimaryColor: (color: string) => void
}

const PrimaryColorContext = createContext<PrimaryColorContextType>({
  primaryColor: 'shalion-green',
  changePrimaryColor: () => {},
})

type PrimaryColorProviderProps = PropsWithChildren<{
  initialColor?: string
}>

const PrimaryColorProvider = (props: PrimaryColorProviderProps) => {
  const { children, initialColor = 'shalion-green' } = props
  const [primaryColor, setPrimaryColor] = useState<string>(() =>
    getStoredPrimaryColor(initialColor),
  )

  const changePrimaryColor = useCallback((color: string) => {
    setPrimaryColor(color)
  }, [])

  useEffect(() => {
    globalThis.localStorage.setItem(PRIMARY_COLOR_STORAGE_KEY, primaryColor)
  }, [primaryColor])

  const value = useMemo(
    () => ({ primaryColor, changePrimaryColor }),
    [primaryColor, changePrimaryColor],
  )

  return (
    <PrimaryColorContext.Provider value={value}>
      <MantineProvider
        cssVariablesResolver={cssVariablesResolver}
        colorSchemeManager={colorSchemeManager}
        defaultColorScheme='light'
        theme={{ ...theme, primaryColor }}
      >
        {children}
      </MantineProvider>
    </PrimaryColorContext.Provider>
  )
}

export const usePrimaryColor = () => {
  const context = useContext(PrimaryColorContext)
  if (!context) {
    throw new Error('usePrimaryColor must be used within a PrimaryColorProvider')
  }
  return context
}

export default PrimaryColorProvider
